// Boite de reception (02/10, demande de Louka) : l'entree « Boîte de réception » de la colonne porte le
// nombre de non-lus et ouvre une petite fenetre, comme les fenetres de Dust, au lieu d'une page entiere
// de l'app. Un clic sur une notification la marque lue, ferme la fenetre et, si elle mene a une
// conversation du chat (pont WhatsApp, « dust:<sId> »), l'ouvre avec le routeur du chat. Sans
// destination (ancien moteur), elle est seulement marquee lue. La page Assistant de l'app est
// prevenue a chaque marquage : elle relit sa boite et renvoie un modele a jour.
// Contrat avec l'app : front/lib/compta/boite_de_reception.ts.

import type {
  BoiteDuModele,
  NotificationBoite,
} from "@app/lib/compta/boite_de_reception";
import {
  lireNotifications,
  marquerLue,
  marquerToutLu,
  prevenirLApp,
  tempsRelatif,
} from "@app/lib/compta/boite_de_reception";
import { useAppRouter } from "@app/lib/platform";
import { getConversationRoute } from "@app/lib/utils/router";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  cn,
  Inbox01,
  NavigationListItem,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@dust-tt/sparkle";
import { useCallback, useEffect, useId, useState } from "react";

type Recuperer = (url: string, init?: RequestInit) => Promise<Response>;

type Etat =
  | { statut: "chargement" }
  | { statut: "erreur" }
  | { statut: "pret"; elements: NotificationBoite[] };

function fenetre() {
  return typeof window === "undefined"
    ? undefined
    : (window as unknown as Parameters<typeof prevenirLApp>[0]);
}

function recupererParDefaut(url: string, init?: RequestInit) {
  return fetch(url, init);
}

export function BoiteDeReception({
  owner,
  boite,
  recuperer = recupererParDefaut,
}: {
  owner: LightWorkspaceType;
  boite: BoiteDuModele;
  /** Pour les tests ; `fetch` par defaut (meme origine que l'app). */
  recuperer?: Recuperer;
}) {
  const router = useAppRouter();
  const titreId = useId();
  const [ouverte, setOuverte] = useState(false);
  const [etat, setEtat] = useState<Etat>({ statut: "chargement" });
  // Le compteur suit les marquages sans attendre l'app ; un nouveau modele de l'app le remplace.
  const [nonLusLocal, setNonLusLocal] = useState<number | null>(null);
  const [nonLusDuModele, setNonLusDuModele] = useState(boite.nonLus);
  if (nonLusDuModele !== boite.nonLus) {
    setNonLusDuModele(boite.nonLus);
    setNonLusLocal(null);
  }
  const nonLus = nonLusLocal ?? boite.nonLus;

  const charger = useCallback(async () => {
    setEtat((e) => (e.statut === "pret" ? e : { statut: "chargement" }));
    const r = await lireNotifications(recuperer, boite.api);
    if (!r) {
      setEtat({ statut: "erreur" });
      return;
    }
    setEtat({ statut: "pret", elements: r.elements });
    setNonLusLocal(r.nonLus);
  }, [recuperer, boite.api]);

  useEffect(() => {
    if (ouverte) {
      void charger();
    }
  }, [ouverte, charger]);

  const ouvrirNotification = async (n: NotificationBoite) => {
    setOuverte(false);
    if (!n.lu) {
      setEtat((e) =>
        e.statut === "pret"
          ? {
              ...e,
              elements: e.elements.map((x) =>
                x.id === n.id ? { ...x, lu: true } : x
              ),
            }
          : e
      );
      setNonLusLocal(Math.max(0, nonLus - 1));
    }
    if (n.conversation) {
      void router.push(getConversationRoute(owner.sId, n.conversation));
    }
    if (!n.lu) {
      await marquerLue(recuperer, boite.api, n.id);
      prevenirLApp(fenetre());
    }
  };

  const toutMarquerLu = async () => {
    setEtat((e) =>
      e.statut === "pret"
        ? { ...e, elements: e.elements.map((x) => ({ ...x, lu: true })) }
        : e
    );
    setNonLusLocal(0);
    await marquerToutLu(recuperer, boite.api);
    prevenirLApp(fenetre());
  };

  return (
    <PopoverRoot open={ouverte} onOpenChange={setOuverte}>
      <PopoverTrigger asChild>
        <NavigationListItem
          label="Boîte de réception"
          icon={Inbox01}
          count={nonLus}
          selected={ouverte}
          role="button"
          tabIndex={0}
          aria-label={
            nonLus > 0
              ? `Boîte de réception, ${nonLus} non lue${nonLus > 1 ? "s" : ""}`
              : "Boîte de réception"
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOuverte((o) => !o);
            }
          }}
        />
      </PopoverTrigger>
      <PopoverContent
        role="dialog"
        aria-labelledby={titreId}
        side="right"
        align="start"
        className="flex w-80 flex-col p-0"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <span
            id={titreId}
            className="text-sm font-semibold text-foreground dark:text-foreground-night"
          >
            Boîte de réception
          </span>
          <Button
            label="Tout marquer lu"
            variant="ghost"
            size="xs"
            disabled={nonLus === 0}
            onClick={() => void toutMarquerLu()}
          />
        </div>
        <div className="max-h-[min(60dvh,420px)] overflow-y-auto py-1">
          {etat.statut === "chargement" && (
            <p className="px-3 py-4 text-sm text-muted-foreground dark:text-muted-foreground-night">
              Chargement…
            </p>
          )}
          {etat.statut === "erreur" && (
            <p className="px-3 py-4 text-sm text-muted-foreground dark:text-muted-foreground-night">
              Impossible de charger les notifications.
            </p>
          )}
          {etat.statut === "pret" && etat.elements.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground dark:text-muted-foreground-night">
              Rien de nouveau.
            </p>
          )}
          {etat.statut === "pret" &&
            etat.elements.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => void ouvrirNotification(n)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted-background focus-visible:bg-muted-background focus-visible:outline-hidden dark:hover:bg-muted-background-night"
              >
                <span className="mt-1.5 flex h-2 w-2 shrink-0">
                  {!n.lu && (
                    <span
                      data-testid="point-non-lu"
                      className="h-2 w-2 rounded-full bg-highlight-500"
                    />
                  )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    className={cn(
                      "truncate text-sm text-foreground dark:text-foreground-night",
                      !n.lu && "font-semibold"
                    )}
                  >
                    {n.titre}
                  </span>
                  {n.extrait && (
                    <span className="line-clamp-2 text-xs text-muted-foreground dark:text-muted-foreground-night">
                      {n.extrait}
                    </span>
                  )}
                  {n.creeLe && (
                    <span className="text-xs text-faint dark:text-faint-night">
                      {tempsRelatif(n.creeLe)}
                    </span>
                  )}
                </span>
              </button>
            ))}
        </div>
      </PopoverContent>
    </PopoverRoot>
  );
}
